/**
 * Navigation Migration Configuration
 *
 * Centralized configuration for navigation layout control using feature flags.
 * Determines default layout, toggle visibility, and toggle label based on REBRANDING.navbar feature flag.
 *
 * Feature Flag Behavior:
 * - REBRANDING.navbar === true: New navigation visible, toggle shown with "Old Navigation" label
 * - REBRANDING.navbar === false: Old navigation only, toggle hidden
 *
 * Environment Configuration:
 * - Staging/Dev: navbar: true (new navigation enabled)
 * - Production: navbar: false (old navigation only)
 */

export type NavigationLayout = 'old' | 'new';

export type MigrationConfig = {
	defaultLayout: NavigationLayout;
	showToggle: boolean;
	toggleLabel: string | null;
};

/**
 * Get navigation migration configuration based on REBRANDING.navbar feature flag
 *
 * @returns MigrationConfig object with layout, toggle visibility, and label
 */
export function getNavigationMigrationConfig(): MigrationConfig {
	// eslint-disable-next-line no-process-env
	const rebrandingNavbar = process.env.REBRANDING.navbar;

	if (rebrandingNavbar === false) {
		// Feature flag disabled: Force old navigation, hide toggle
		return {
			defaultLayout: 'old',
			showToggle: false,
			toggleLabel: null,
		};
	}

	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	if (rebrandingNavbar === true) {
		// Feature flag enabled: New navigation default, toggle visible
		return {
			defaultLayout: 'new',
			showToggle: true,
			toggleLabel: 'Old Navigation',
		};
	}

	// Fallback: If feature flag is undefined (shouldn't happen in production),
	// default to old navigation for safety
	return {
		defaultLayout: 'old',
		showToggle: false,
		toggleLabel: null,
	};
}
